import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LumenMascot } from "@/components/LumenMascot";
import { FlagIcon } from "@/components/FlagIcon";
import { SUPPORTED_LANGUAGES, persistLanguage, normalizeLanguage, type AppLanguage } from "@/i18n/config";

export const Route = createFileRoute("/onboarding")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Onboarding — Personalizza Lumen" },
      { name: "description", content: "Rispondi a poche domande per personalizzare Lumen: livello, stile di apprendimento e obiettivi di studio." },
      { property: "og:title", content: "Onboarding — Personalizza Lumen" },
      { property: "og:description", content: "Personalizza il tuo tutor AI: livello, stile e obiettivi di studio." },
      { property: "og:url", content: "https://lumen-ai.ltd/onboarding" },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex,follow" },
    ],
    links: [{ rel: "canonical", href: "https://lumen-ai.ltd/onboarding" }],
  }),
  component: Onboarding,
});

type Role = "student" | "teacher" | "parent";

type Data = {
  language: AppLanguage;
  account_type: Role;
  account_roles: Role[];
  is_adult: boolean;
  full_name: string;
  education_level: string;
  school_name: string;
  goals: string;
  daily_study_hours: number;
  learning_style: string;
  has_dsa: boolean;
  has_adhd: boolean;
  has_bes: boolean;
  rest_days: number[];
};

function getLevels(t: (k: string) => string) {
  return [
    { v: "media", l: t("onboarding.levels.media") },
    { v: "superiore", l: t("onboarding.levels.superiore") },
    { v: "universita", l: t("onboarding.levels.universita") },
    { v: "concorso", l: t("onboarding.levels.concorso") },
    { v: "corso", l: t("onboarding.levels.corso") },
  ];
}
function getStyles(t: (k: string) => string) {
  return [
    { v: "visivo", l: t("onboarding.styles.visivo.l"), d: t("onboarding.styles.visivo.d") },
    { v: "uditivo", l: t("onboarding.styles.uditivo.l"), d: t("onboarding.styles.uditivo.d") },
    { v: "cinestetico", l: t("onboarding.styles.cinestetico.l"), d: t("onboarding.styles.cinestetico.d") },
    { v: "lettura", l: t("onboarding.styles.lettura.l"), d: t("onboarding.styles.lettura.d") },
  ];
}

function Onboarding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState<Data>({
    language: normalizeLanguage(i18n.language),
    account_type: "student",
    account_roles: ["student"],
    is_adult: false,
    full_name: "",
    education_level: "",
    school_name: "",
    goals: "",
    daily_study_hours: 2,
    learning_style: "",
    has_dsa: false,
    has_adhd: false,
    has_bes: false,
    rest_days: [],
  });

  const levels = getLevels(t);
  const styles = getStyles(t);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data.user) navigate({ to: "/auth" });
    });
  }, [navigate]);

  async function pickLanguage(code: AppLanguage) {
    setD((prev) => ({ ...prev, language: code }));
    persistLanguage(code);
    await i18n.changeLanguage(code);
  }

  // Docenti e famiglie non hanno bisogno delle domande sullo studio personale.
  const steps = d.account_type === "student" ? 9 : 3;

  async function finish() {
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ ...d, account_roles: d.is_adult ? d.account_roles.slice(0, 2) : [d.account_type], rest_days: [...d.rest_days].sort(), onboarding_completed: true })
      .eq("id", u.user.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(t("onboarding.toasts.ready"));
    await queryClient.invalidateQueries();
    navigate({ to: d.account_type === "teacher" ? "/teacher" : d.account_type === "parent" ? "/family" : "/" });
  }

  const next = () => setStep((s) => Math.min(steps - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const question = (() => {
    switch (step) {
      case 0:
        return { title: t("onboarding.lang.title"), subtitle: t("onboarding.lang.subtitle") };
      case 1:
        return { title: t("teacher.role.question"), subtitle: t("teacher.role.subtitle") };
      case 2:
        return { title: t("onboarding.step0.title"), subtitle: t("onboarding.step0.subtitle") };
      case 3:
        return { title: t("onboarding.step1.title"), subtitle: "" };
      case 4:
        return { title: t("onboarding.step2.title"), subtitle: t("onboarding.step2.subtitle") };
      case 5:
        return { title: t("onboarding.step3.title"), subtitle: t("onboarding.step3.subtitle") };
      case 6:
        return { title: t("onboarding.step4.title"), subtitle: "" };
      case 7:
        return { title: t("onboarding.step5.title"), subtitle: t("onboarding.step5.subtitle") };
      default:
        return { title: t("lumen.onboarding.restTitle"), subtitle: t("lumen.onboarding.restSubtitle") };
    }
  })();

  return (
    <div className="min-h-dvh bg-background px-[max(env(safe-area-inset-left),1.25rem)] pr-[max(env(safe-area-inset-right),1.25rem)] pt-[max(env(safe-area-inset-top),2.5rem)] pb-[max(env(safe-area-inset-bottom),6rem)]">
      <div className="mx-auto max-w-md">
        <Progress value={((step + 1) / steps) * 100} className="mb-4 h-1.5" />
        <div className="mb-4 flex items-start gap-3">
          <LumenMascot mood={step === steps - 1 ? "celebrate" : "happy"} size={72} className="shrink-0" />
          <motion.div
            key={`bubble-${step}`}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25 }}
            className="relative mt-2 min-w-0 flex-1 rounded-2xl rounded-tl-sm bg-muted/70 px-4 py-3 text-foreground"
            aria-live="polite"
          >
            <span
              aria-hidden
              className="absolute -left-1.5 top-3 h-3 w-3 rotate-45 rounded-[3px] bg-muted/70"
            />
            {step === 0 && (
              <p className="mb-1 text-xs text-muted-foreground">{t("lumen.onboarding.intro")}</p>
            )}
            <p className="break-words text-base font-semibold leading-snug">{question.title}</p>
            {question.subtitle ? (
              <p className="mt-1 break-words text-xs text-muted-foreground">{question.subtitle}</p>
            ) : null}
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            <Card className="card-soft border-0 p-6">
              {step === 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {SUPPORTED_LANGUAGES.map((l) => {
                    const on = d.language === l.code;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => void pickLanguage(l.code)}
                        aria-pressed={on}
                        className={`flex items-center gap-2 rounded-xl border p-3 text-left transition ${on ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                      >
                        <FlagIcon code={l.code} className="h-5 w-7 shrink-0 rounded-[3px] ring-1 ring-black/10" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold">{l.nativeName}</span>
                          <span className="block truncate text-xs text-muted-foreground">{l.name}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {step === 1 && (
                <>
                  <div className="grid grid-cols-1 gap-2">
                    {(["student", "teacher", "parent"] as const).map((r) => {
                      const on = d.account_roles.includes(r);
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() => {
                            if (on) {
                              if (d.account_roles.length === 1) return;
                              const next = d.account_roles.filter((x) => x !== r);
                              setD({ ...d, account_roles: next, account_type: next[0]! });
                              return;
                            }
                            if (!d.is_adult) {
                              setD({ ...d, account_roles: [r], account_type: r });
                              return;
                            }
                            if (d.account_roles.length >= 2) {
                              toast.error(t("roleui.switch.max"));
                              return;
                            }
                            setD({ ...d, account_roles: [...d.account_roles, r] });
                          }}
                          className={`rounded-xl border p-3 text-left transition ${on ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                          aria-pressed={on}
                        >
                          <div className="text-sm font-semibold">{t(`teacher.role.${r}`)}</div>
                          <div className="text-xs text-muted-foreground">{t(`teacher.role.${r}Desc`)}</div>
                        </button>
                      );
                    })}
                  </div>
                  <label className="mt-4 flex items-start gap-2 rounded-2xl bg-muted/60 p-3">
                    <Checkbox
                      checked={d.is_adult}
                      onCheckedChange={(v) => {
                        const adult = v === true;
                        setD({
                          ...d,
                          is_adult: adult,
                          account_roles: adult ? d.account_roles : [d.account_type],
                        });
                      }}
                      aria-label={t("roleui.switch.adult")}
                    />
                    <span className="min-w-0">
                      <span className="block break-words text-sm font-medium">{t("roleui.switch.adult")}</span>
                      <span className="block break-words text-xs text-muted-foreground">{t("roleui.switch.addHint")}</span>
                    </span>
                  </label>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="space-y-2">
                    <Label>{t("onboarding.step0.label")}</Label>
                    <Input value={d.full_name} onChange={(e) => setD({ ...d, full_name: e.target.value })} placeholder="Mario" />
                  </div>
                </>
              )}
              {step === 3 && (
                <>
                  <div className="grid grid-cols-1 gap-2">
                    {levels.map((x) => (
                      <button
                        key={x.v}
                        onClick={() => setD({ ...d, education_level: x.v })}
                        className={`rounded-xl border p-3 text-left text-sm transition ${d.education_level === x.v ? "border-primary bg-primary/10 font-medium" : "border-border hover:bg-muted"}`}
                      >
                        {x.l}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {step === 4 && (
                <>
                  <div className="space-y-2">
                    <Label>{t("onboarding.step2.label")}</Label>
                    <Input value={d.school_name} onChange={(e) => setD({ ...d, school_name: e.target.value })} placeholder={t("onboarding.step2.placeholder")} />
                  </div>
                </>
              )}
              {step === 5 && (
                <>
                  <div>
                    <div className="mb-3 text-4xl font-semibold text-primary">{d.daily_study_hours}h</div>
                    <Slider
                      value={[d.daily_study_hours]}
                      onValueChange={(v) => setD({ ...d, daily_study_hours: v[0] })}
                      min={0.5}
                      max={10}
                      step={0.5}
                    />
                  </div>
                </>
              )}
              {step === 6 && (
                <>
                  <div className="grid grid-cols-1 gap-2">
                    {styles.map((x) => (
                      <button
                        key={x.v}
                        onClick={() => setD({ ...d, learning_style: x.v })}
                        className={`rounded-xl border p-3 text-left transition ${d.learning_style === x.v ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                      >
                        <div className="text-sm font-semibold">{x.l}</div>
                        <div className="text-xs text-muted-foreground">{x.d}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {step === 7 && (
                <>
                  <div className="space-y-3">
                    {[
                      { key: "has_dsa" as const, l: t("onboarding.step5.dsa") },
                      { key: "has_adhd" as const, l: t("onboarding.step5.adhd") },
                      { key: "has_bes" as const, l: t("onboarding.step5.bes") },
                    ].map((x) => (
                      <label key={x.key} className="flex items-center gap-3 rounded-xl border border-border p-3 text-sm">
                        <Checkbox
                          checked={d[x.key]}
                          onCheckedChange={(v) =>
                            setD({ ...d, [x.key]: !!v })
                          }
                        />
                        <span>{x.l}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-3 rounded-xl border border-border p-3 text-sm">
                      <Checkbox
                        checked={!d.has_dsa && !d.has_adhd && !d.has_bes}
                        onCheckedChange={(v) => {
                          if (v) setD({ ...d, has_dsa: false, has_adhd: false, has_bes: false });
                        }}
                      />
                      <span>{t("onboarding.step5.none")}</span>
                    </label>
                  </div>
                </>
              )}
              {step === 8 && (
                <>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
                    {Array.from({ length: 7 }, (_, dow) => {
                      const label = new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(
                        new Date(Date.UTC(2024, 0, 7 + dow)),
                      );
                      const active = d.rest_days.includes(dow);
                      return (
                        <button
                          key={dow}
                          type="button"
                          onClick={() =>
                            setD({
                              ...d,
                              rest_days: active ? d.rest_days.filter((x) => x !== dow) : [...d.rest_days, dow],
                            })
                          }
                          className={`truncate rounded-xl px-1 py-3 text-xs font-semibold capitalize transition ${active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70 hover:opacity-80"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">{t("lumen.rest.hint")}</p>
                </>
              )}
            </Card>
          </motion.div>
        </AnimatePresence>

        <div className="mt-5 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={back} disabled={step === 0}>{t("onboarding.back")}</Button>
          {step < steps - 1 ? (
            <Button onClick={next} className="rounded-xl">{t("onboarding.continue")}</Button>
          ) : (
            <Button onClick={finish} disabled={saving} className="rounded-xl">
              {saving ? t("onboarding.saving") : t("onboarding.finish")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
